import { useEffect, useState } from 'react';
import type { OutletRef, ReceiptListItem } from '@kassa/shared';
import { api, ApiError } from '../api';
import { NotesPanel } from '../components/NotesPanel';
import { formatDate, formatMoney, kop } from '../format';
import { DateField } from '../components/DateField';
import { DataTable, NewButton, PageHeader, Pager, QuickDates, SearchPanel, useUrlFilters, type Column } from '../components/ListPage';
import { Receipt } from './Receipt';
import { CancelSale, cancelSummary } from '../components/CancelSale';

const PAGE = 25;
const KEYS = ['p', 'from', 'to', 'o', 'k', 'st', 'q', 'page'] as const;

function bakuDay(offsetDays = 0): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Baku' }).format(new Date(Date.now() + offsetDays * 86_400_000));
}

// Satışlar (KT-99 şablonu): çek və qaytarma sənədləri — tez tarix düymələri (bu gün, bu ay, son 30 gün), axtarış paneli
// (tarix aralığı, obyekt, növ, nömrə/müştəri), səhifə cəmi sətri. Çekə klik — çekin surəti.
export function Receipts({ onNewSale, canCancel = false }: { onNewSale?: () => void; canCancel?: boolean }) {
  const [f, setF] = useUrlFilters(KEYS);
  const quick = f.p || (f.from || f.to ? '' : 'day');
  const [draft, setDraft] = useState({ from: f.from, to: f.to, o: f.o, k: f.k, st: f.st, q: f.q });
  const [cancelling, setCancelling] = useState<ReceiptListItem | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [reload, setReload] = useState(0);
  const [outlets, setOutlets] = useState<OutletRef[]>([]);
  const [items, setItems] = useState<ReceiptListItem[]>([]);
  const [truncated, setTruncated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState<string | null>(null);
  const page = Number(f.page) || 1;

  useEffect(() => {
    api.outlets().then(setOutlets).catch(() => setOutlets([]));
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    const range =
      quick === 'day' ? { period: 'day' as const } : quick === 'month' ? { period: 'month' as const } : quick === '30d' ? { from: bakuDay(-29), to: bakuDay() } : { from: f.from || undefined, to: f.to || undefined };
    api
      .reportReceipts({ ...range, outletId: f.o || undefined, q: f.q || undefined, cancelled: f.st ? '1' : undefined })
      .then((r) => {
        if (cancelled) return;
        setItems(r.items);
        setTruncated(r.truncated);
      })
      .catch((err: unknown) => !cancelled && setError(err instanceof ApiError ? err.message : 'Siyahı yüklənmədi'))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [quick, f.from, f.to, f.o, f.q, f.st, reload]);

  // Status: boş — aktiv, cancelled — yalnız ləğv olunanlar, all — hamısı (API ləğvliləri ?cancelled=1 ilə verir)
  const rows = items.filter((r) => (!f.k || r.kind === f.k) && (f.st === 'all' || (f.st === 'cancelled' ? r.status === 'cancelled' : r.status !== 'cancelled')));
  const shown = rows.slice((page - 1) * PAGE, page * PAGE);
  // Qaytarma mənfi; 0 «-0,00» görünməsin
  const sign = (r: ReceiptListItem, v: number) => (r.kind === 'QAY' && v !== 0 ? -v : v);
  // Ləğv olunmuş satış cəmə daxil deyil
  const sum = (pick: (r: ReceiptListItem) => number) => shown.reduce((s, r) => s + (r.status === 'cancelled' ? 0 : kop(sign(r, pick(r)))), 0) / 100;

  const columns: Column<ReceiptListItem>[] = [
    {
      key: 'no',
      label: 'Nömrə',
      mono: true,
      render: (r) => (
        <button type="button" className="cell-link" onClick={() => setOpen(r.id)}>
          {r.displayNo}
        </button>
      ),
    },
    { key: 'at', label: 'Tarix', minor: true, render: (r) => formatDate(r.postedAt) },
    {
      key: 'kind',
      label: 'Növ',
      render: (r) =>
        r.status === 'cancelled' ? (
          <span className="badge badge-bad">Ləğv edildi</span>
        ) : (
          <span className={r.kind === 'QAY' ? 'badge badge-bad' : 'badge badge-ok'}>{r.kind === 'QAY' ? 'Qaytarma' : 'Satış'}</span>
        ),
    },
    { key: 'cust', label: 'Müştəri', minor: true, render: (r) => r.customer ?? '—' },
    { key: 'cashier', label: 'Kassir', minor: true, render: (r) => r.cashier ?? '—' },
    { key: 'cash', label: 'Nağd', align: 'right', mono: true, minor: true, render: (r) => formatMoney(sign(r, r.cash)) },
    { key: 'card', label: 'Kart', align: 'right', mono: true, minor: true, render: (r) => formatMoney(sign(r, r.card)) },
    // KT-90: hər ödəniş parçası ayrıca sütunda (köçürmə, nisyə də); fiskal kod — e-kassa (KT-92) qoşulana qədər boş
    { key: 'transfer', label: 'Köçürmə', align: 'right', mono: true, minor: true, render: (r) => (r.transfer ? formatMoney(sign(r, r.transfer)) : '—') },
    { key: 'bonus', label: 'Bonus', align: 'right', mono: true, minor: true, render: (r) => (r.bonus ? formatMoney(sign(r, r.bonus)) : '—') },
    { key: 'credit', label: 'Nisyə', align: 'right', mono: true, minor: true, render: (r) => (r.credit ? formatMoney(sign(r, r.credit)) : '—') },
    { key: 'fiscal', label: 'Fiskal kod', mono: true, minor: true, render: (r) => r.fiscalId ?? '—' },
    { key: 'total', label: 'Cəmi', align: 'right', mono: true, render: (r) => formatMoney(sign(r, r.total)) },
  ];

  return (
    <main className="page page-list">
      <PageHeader title="Satışlar" actions={onNewSale ? <NewButton label="Yeni satış" onClick={onNewSale} /> : undefined} />

      <div className="list-bar">
        <QuickDates value={quick} onChange={(p) => setF({ p, from: '', to: '', page: '' })} />
      </div>

      <SearchPanel
        defaultOpen={Boolean(f.from || f.to || f.o || f.k || f.st || f.q)}
        onSearch={() => setF({ p: draft.from || draft.to ? '' : f.p, from: draft.from, to: draft.to, o: draft.o, k: draft.k, st: draft.st, q: draft.q.trim(), page: '' })}
        onClear={() => {
          setDraft({ from: '', to: '', o: '', k: '', st: '', q: '' });
          setF({ p: '', from: '', to: '', o: '', k: '', st: '', q: '', page: '' });
        }}
      >
        <label className="field">
          Tarixdən
          <DateField value={draft.from} onChange={(v) => setDraft({ ...draft, from: v })} />
        </label>
        <label className="field">
          Tarixədək
          <DateField value={draft.to} onChange={(v) => setDraft({ ...draft, to: v })} />
        </label>
        {outlets.length > 1 && (
          <label className="field">
            Obyekt
            <select value={draft.o} onChange={(e) => setDraft({ ...draft, o: e.target.value })}>
              <option value="">Hamısı</option>
              {outlets.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.name}
                </option>
              ))}
            </select>
          </label>
        )}
        <label className="field">
          Növ
          <select value={draft.k} onChange={(e) => setDraft({ ...draft, k: e.target.value })}>
            <option value="">Hamısı</option>
            <option value="SAT">Satış</option>
            <option value="QAY">Qaytarma</option>
          </select>
        </label>
        <label className="field">
          Status
          <select value={draft.st} onChange={(e) => setDraft({ ...draft, st: e.target.value })}>
            <option value="">Aktiv</option>
            <option value="cancelled">Ləğv olunan</option>
            <option value="all">Hamısı</option>
          </select>
        </label>
        <label className="field">
          Nömrə və ya müştəri
          <input value={draft.q} onChange={(e) => setDraft({ ...draft, q: e.target.value })} maxLength={60} />
        </label>
      </SearchPanel>

      {truncated && <p className="muted small">Siyahı uzundur — yalnız ilk sənədlər göstərilir. Tarix aralığını daraldın.</p>}

      <DataTable
        label="Satışlar"
        columns={columns}
        rows={shown}
        rowKey={(r) => r.id}
        loading={loading}
        error={error}
        empty={<p className="muted">Bu dövrdə satış yoxdur.</p>}
        footer={{
          no: 'Səhifə cəmi',
          cash: formatMoney(sum((r) => r.cash)),
          card: formatMoney(sum((r) => r.card)),
          transfer: formatMoney(sum((r) => r.transfer)),
          bonus: formatMoney(sum((r) => r.bonus)),
          credit: formatMoney(sum((r) => r.credit)),
          total: formatMoney(sum((r) => r.total)),
        }}
      />
      <Pager page={page} limit={PAGE} total={rows.length} unit="sənəd" onPage={(p) => setF({ page: p > 1 ? String(p) : '' })} />

      {notice && (
        <p className="ok" role="status">
          {notice}
        </p>
      )}
      {open && (
        <section className="card card-wide" aria-label="Çek">
          <div className="toolbar">
            <button type="button" className="link-btn" onClick={() => setOpen(null)}>
              Bağla
            </button>
            {canCancel && items.find((r) => r.id === open && r.kind === 'SAT' && r.status === 'posted') && (
              <button type="button" className="btn-danger btn-small" onClick={() => setCancelling(items.find((r) => r.id === open) ?? null)}>
                Satışı ləğv et
              </button>
            )}
          </div>
          <Receipt id={open} copy />
          <NotesPanel target={{ docId: open }} />
        </section>
      )}
      {cancelling && (
        <CancelSale
          saleId={cancelling.id}
          displayNo={cancelling.displayNo}
          onClose={() => setCancelling(null)}
          onDone={(r) => {
            setCancelling(null);
            setOpen(null);
            setNotice(cancelSummary(r));
            setReload((n) => n + 1);
          }}
        />
      )}
    </main>
  );
}
